import React from 'react';
import { styled } from '@compiled/react';
import type { PreloadedQuery } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { BreadcrumbRenderer } from '@atlassian/jira-breadcrumbs/src';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar';
import { useIntl } from '@atlassian/jira-intl';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { useShowQuickFilters } from '@atlassian/jira-software-view-settings';
import { useBacklogSelector } from '../../state';
import type { State } from '../../state/reducers/types';
import { getBoardType, getIsCompanyManaged } from '../../state/selectors/app';
import { canEditBoardConfig } from '../../state/selectors/permissions';
import { ActionsBar } from './actions-bar';
import ControlsBar from './controls-bar';
import FilterSelectionBarContainer from './controls-bar/filter-selection-bar';
import messages from './messages';

export type Props = {
	customFiltersQueryRef?: PreloadedQuery<viewBacklogCustomFiltersQuery> | null;
	versionFilterRelayQueryRef?: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null;
};

export const getIsMenuVisible = (state: State) =>
	canEditBoardConfig(state) || getIsCompanyManaged(state);

function HeaderNew(props: Props) {
	const { customFiltersQueryRef, versionFilterRelayQueryRef } = props;
	const isMenuVisible = useBacklogSelector(getIsMenuVisible);
	const isFilterSelectionBarOpen = useShowQuickFilters();

	return (
		<>
			<PageTopBar
				bottomBar={
					<Box
						xcss={
							ff('sea-2744-backlog-dropdown-bugfix_px6bs')
								? controlsBarWrapperNew
								: controlsBarWrapperOld
						}
					>
						<ControlsBar
							customFiltersQueryRef={customFiltersQueryRef}
							versionFilterRelayQueryRef={versionFilterRelayQueryRef}
						/>
					</Box>
				}
				actions={<ActionsBar isMenuVisible={isMenuVisible} />}
				wrapAtMd
			/>
			{isFilterSelectionBarOpen && <FilterSelectionBarContainer />}
		</>
	);
}

function HeaderOld(props: Props) {
	const { customFiltersQueryRef, versionFilterRelayQueryRef } = props;
	const isMenuVisible = useBacklogSelector(getIsMenuVisible);
	const boardType = useBacklogSelector(getBoardType);
	const { formatMessage } = useIntl();

	return (
		<PageHeader
			truncateTitle
			breadcrumbs={breadcrumbs}
			bottomBar={
				<ControlsBar
					customFiltersQueryRef={customFiltersQueryRef}
					versionFilterRelayQueryRef={versionFilterRelayQueryRef}
				/>
			}
			actions={<ActionsBar isMenuVisible={isMenuVisible} boardType={boardType} />}
		>
			<HeadingStyle>
				<TruncatingTextStyle>{formatMessage(messages.pageTitle)}</TruncatingTextStyle>
			</HeadingStyle>
		</PageHeader>
	);
}

export default componentWithCondition(() => ff('sea-2744-jsw-tabs-nav'), HeaderNew, HeaderOld);

const controlsBarWrapperOld = xcss({
	// enough width to fit expanded search + avatar filter + clear filters button
	minWidth: '480px',
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'end',
	flex: '1 1 auto',
});

const controlsBarWrapperNew = xcss({
	// enough width to fit expanded search + avatar filter + clear filters button
	minWidth: '480px',
	display: 'flex',
	alignItems: 'end',
	flex: '1 1 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BreadcrumbContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.fullscreen': {
		display: 'none',
	},
});

const breadcrumbs = (
	<BreadcrumbContainer data-fullscreen-id="fullscreen-backlog-breadcrumbs">
		<BreadcrumbRenderer />
	</BreadcrumbContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingStyle = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TruncatingTextStyle = styled.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	flex: '0 1 auto',
});
